import { useI18n } from 'vue-i18n'

export function useSEO() {
    const { t, locale } = useI18n()

    const updateMetaTags = (pageData) => {
        const {
            title,
            description,
            keywords = '',
            image = '/heidelbell-logo.png',
            url = window.location.href,
            type = 'website'
        } = pageData

        // Update document title
        document.title = title

        // Update primary meta tags
        updateMetaTag('title', title)
        updateMetaTag('description', description)
        updateMetaTag('keywords', keywords)
        updateMetaTag('author', 'Heidelbell')

        // Update Open Graph tags
        updateMetaTag('og:title', title, 'property')
        updateMetaTag('og:description', description, 'property')
        updateMetaTag('og:image', image, 'property')
        updateMetaTag('og:url', url, 'property')
        updateMetaTag('og:type', type, 'property')
        updateMetaTag('og:locale', locale.value === 'de' ? 'de_DE' : 'en_US', 'property')

        // Update Twitter Card tags
        updateMetaTag('twitter:title', title, 'property')
        updateMetaTag('twitter:description', description, 'property')
        updateMetaTag('twitter:image', image, 'property')
        updateMetaTag('twitter:url', url, 'property')

        // Update canonical URL
        updateCanonical(url)
    }

    const updateMetaTag = (name, content, attribute = 'name') => {
        let meta = document.querySelector(`meta[${attribute}="${name}"]`)
        if (!meta) {
            meta = document.createElement('meta')
            meta.setAttribute(attribute, name)
            document.head.appendChild(meta)
        }
        meta.setAttribute('content', content)
    }

    const updateCanonical = (url) => {
        let canonical = document.querySelector('link[rel="canonical"]')
        if (!canonical) {
            canonical = document.createElement('link')
            canonical.setAttribute('rel', 'canonical')
            document.head.appendChild(canonical)
        }
        canonical.setAttribute('href', url)
    }

    const addStructuredData = (data) => {
        // Remove existing structured data
        const existingScripts = document.querySelectorAll('script[type="application/ld+json"]')
        existingScripts.forEach(script => {
            if (script.dataset.dynamic === 'true') {
                script.remove()
            }
        })

        // Add new structured data
        const script = document.createElement('script')
        script.setAttribute('type', 'application/ld+json')
        script.setAttribute('data-dynamic', 'true')
        script.textContent = JSON.stringify(data, null, 2)
        document.head.appendChild(script)
    }

    const generatePageSEO = (pageKey) => {
        const seoData = {
            home: {
                title: t('seo.home.title'),
                description: t('seo.home.description'),
                keywords: t('seo.home.keywords'),
                type: 'website'
            },
            about: {
                title: t('seo.about.title'),
                description: t('seo.about.description'),
                keywords: t('seo.about.keywords'),
                type: 'website'
            },
            services: {
                title: t('seo.services.title'),
                description: t('seo.services.description'),
                keywords: t('seo.services.keywords'),
                type: 'website'
            },
            contact: {
                title: t('seo.contact.title'),
                description: t('seo.contact.description'),
                keywords: t('seo.contact.keywords'),
                type: 'website'
            },
            privacy: {
                title: t('privacy.seo.title'),
                description: t('privacy.seo.description'),
                keywords: 'datenschutz, DSGVO, privacy policy, Heidelbell',
                type: 'website'
            },
            terms: {
                title: t('terms.seo.title'),
                description: t('terms.seo.description'),
                keywords: 'AGB, terms and conditions, Heidelbell, service terms',
                type: 'website'
            },
            imprint: {
                title: t('imprint.seo.title'),
                description: t('imprint.seo.description'),
                keywords: 'impressum, legal information, Heidelbell, company details',
                type: 'website'
            }
        }

        return seoData[pageKey] || seoData.home
    }

    const generateStructuredData = (pageKey) => {
        const baseData = {
            "@context": "https://schema.org",
            "url": window.location.href
        }

        const structuredData = {
            home: {
                ...baseData,
                "@type": "WebPage",
                "name": t('seo.home.title'),
                "description": t('seo.home.description'),
                "isPartOf": {
                    "@type": "WebSite",
                    "name": "Heidelbell",
                    "url": "https://heidelbell.com"
                }
            },
            about: {
                ...baseData,
                "@type": "AboutPage",
                "name": t('seo.about.title'),
                "description": t('seo.about.description')
            },
            services: {
                ...baseData,
                "@type": "Service",
                "name": "IT Infrastructure & Technology Consulting",
                "description": t('seo.services.description'),
                "provider": {
                    "@type": "Organization",
                    "name": "Heidelbell",
                    "url": "https://heidelbell.com"
                },
                "serviceType": [
                    "ITSM - IT Service Management",
                    "ITAD - IT Asset Disposition",
                    "ITAM - IT Asset Management",
                    "Smart Hands Services",
                    "IT Infrastructure Installation & Maintenance",
                    "Help Desk Support",
                    "Project Management Support",
                    "Software Development & Testing",
                    "Process Optimization",
                    "Data Management",
                    "Cloud Solutions",
                    "Remote Hands",
                    "On-site Support",
                    "IT Operations",
                    "IT Service Delivery"
                ]
            },
            contact: {
                ...baseData,
                "@type": "ContactPage",
                "name": t('seo.contact.title'),
                "description": t('seo.contact.description'),
                "mainEntity": {
                    "@type": "Organization",
                    "name": "Heidelbell",
                    "contactPoint": {
                        "@type": "ContactPoint",
                        "telephone": "+49-176-47050093",
                        "contactType": "customer service",
                        "email": "connect@heidelbell.com"
                    }
                }
            }
        }

        return structuredData[pageKey] || structuredData.home
    }

    return {
        updateMetaTags,
        addStructuredData,
        generatePageSEO,
        generateStructuredData
    }
} 